import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {Observable} from "rxjs";
import {map} from "rxjs/operators";
import {BackendApiService} from "../../../../services/backend-api.service";
import {StaffService} from "../../../../services/staff/staff.service";

const PER_PAGE = 12;

/**
 * Identity of a picked person across entity types — a Vendor and a User can
 * share an id. Items without a type predate multi-entity pickers and are Users.
 */
export function ticketPeopleKey(item: any): string {
  return `${item?.type || 'Users'}:${item?.id}`;
}

const PEOPLE_TYPE_LABELS: Record<string, string> = {
  Users: 'User', Vendors: 'Vendor', Customers: 'Customer', Subcontractors: 'Subcontractor',
};

/** Singular label for a picked person's entity type, e.g. for a chip tooltip. */
export function ticketPeopleTypeLabel(item: any): string {
  return PEOPLE_TYPE_LABELS[item?.type] || 'User';
}

interface PeoplePage {
  items: any[];
  currentPage: number;
  hasMore: boolean;
}

@Component({
    selector: 'app-ticket-user-popup',
    templateUrl: './ticket-user-popup.component.html',
    styleUrls: ['./ticket-user-popup.component.scss'],
    standalone: false
})
export class TicketUserPopupComponent implements OnInit {
  public type: any;
  public index: any;

  /**
   * Multi-select mode (checklist Observers/Participants): rows toggle instead of
   * closing, picks survive tab switches, and Add returns them all at once.
   * Without it the popup keeps its original click-to-pick-one behaviour.
   */
  multiple = false;
  /** Picks made in this session, keyed by ticketPeopleKey. */
  selected = new Map<string, any>();
  /** Already associated before the popup opened; shown as added, not re-pickable. */
  private existingKeys = new Set<string>();

  usersList: any[] = [];
  vendorsList: any[] = [];
  customersList: any[] = [];
  subcontractorsList: any[] = [];

  attachAbleUser: any = ['Users','Vendors','Customers','Subcontractors'];
  selectedIndex: any = 0;

  TYPE_USER: any = 'Users';
  TYPE_VENDORS: any = 'Vendors';
  TYPE_CUSTOMERS: any = 'Customers';
  TYPE_SUBCONTRACTORS: any = 'Subcontractors';

  /**
   * Only these two have confirmed backend pagination support (page/per_page +
   * a meta envelope). Customers/Subcontractors search still returns a flat,
   * unpaginated list, so scrolling never requests a "next page" for them.
   */
  private readonly PAGINATED_TYPES = [this.TYPE_USER, this.TYPE_VENDORS];

  isLoading: Record<string, boolean> = { Users: false, Vendors: false, Customers: false, Subcontractors: false };
  isLoadingMore: Record<string, boolean> = { Users: false, Vendors: false, Customers: false, Subcontractors: false };
  currentPage: Record<string, number> = { Users: 1, Vendors: 1, Customers: 1, Subcontractors: 1 };
  hasMore: Record<string, boolean> = { Users: true, Vendors: true, Customers: true, Subcontractors: true };
  searchTerm: Record<string, string> = { Users: '', Vendors: '', Customers: '', Subcontractors: '' };

  readonly skeletonRows = Array.from({ length: 6 });
  readonly loadMoreSkeletonRows = Array.from({ length: 2 });

  constructor(
    public dialogRef: MatDialogRef<TicketUserPopupComponent>,
    public backendApiService: BackendApiService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public staffService: StaffService,
  ) {
    this.type = this.data?.type;
    this.index = this.data?.index
    if (this.data?.attachAbleUser){
      this.attachAbleUser = this.data?.attachAbleUser;
    }
    this.multiple = !!this.data?.multiple;
    (this.data?.selected || []).forEach((item: any) => this.existingKeys.add(ticketPeopleKey(item)));
  }

  ngOnInit(): void {
    // Populate the first visible tab up front, same as before, so the popup
    // never opens blank.
    this.loadTab(this.visibleTabs[0]);
  }

  /**
   * Tabs render in this fixed order but are filtered by attachAbleUser, so the
   * visible index is an index into the filtered list, not the full one.
   */
  private get visibleTabs(): string[] {
    return [this.TYPE_USER, this.TYPE_VENDORS, this.TYPE_CUSTOMERS, this.TYPE_SUBCONTRACTORS]
      .filter((type: string) => this.checkAttachAbleUser(type));
  }

  private listFor(type: string): any[] {
    switch (type) {
      case this.TYPE_VENDORS: return this.vendorsList;
      case this.TYPE_CUSTOMERS: return this.customersList;
      case this.TYPE_SUBCONTRACTORS: return this.subcontractorsList;
      default: return this.usersList;
    }
  }

  private setListFor(type: string, items: any[]): void {
    switch (type) {
      case this.TYPE_VENDORS: this.vendorsList = items; break;
      case this.TYPE_CUSTOMERS: this.customersList = items; break;
      case this.TYPE_SUBCONTRACTORS: this.subcontractorsList = items; break;
      default: this.usersList = items;
    }
  }

  /**
   * An empty term is deliberate throughout: the backend wraps it as LIKE '%%',
   * which matches every row, so this doubles as "show all" for the browse list.
   */
  private fetchPage(type: string, search: string, page: number): void {
    const isFirstPage = page === 1;
    this.searchTerm[type] = search;

    if (isFirstPage) {
      this.isLoading[type] = true;
      this.setListFor(type, []);
    } else {
      this.isLoadingMore[type] = true;
    }

    this.requestPage(type, search, page).subscribe({
      next: ({ items, currentPage, hasMore }: PeoplePage) => {
        const existing = isFirstPage ? [] : this.listFor(type);
        this.setListFor(type, [...existing, ...items]);
        this.currentPage[type] = currentPage;
        this.hasMore[type] = hasMore;
      },
      complete: () => {
        this.isLoading[type] = false;
        this.isLoadingMore[type] = false;
      },
    });
  }

  private requestPage(type: string, search: string, page: number): Observable<PeoplePage> {
    if (type === this.TYPE_USER) {
      return this.backendApiService.getApi('staff/search', { search, page, per_page: PER_PAGE })
        .pipe(map((response: any) => {
          const items = this.staffService.processStaff(response.data);
          const currentPage = response.meta?.current_page ?? page;
          const hasMore = currentPage < (response.meta?.last_page ?? currentPage);
          return { items, currentPage, hasMore };
        }));
    }

    if (type === this.TYPE_VENDORS) {
      return this.backendApiService.postApi('vendors/search', { search, page, per_page: PER_PAGE })
        .pipe(map((response: any) => {
          // Paginated vendor responses come back double-wrapped as
          // { data: { data: [...], current_page, last_page } }.
          const envelope = response?.data?.data ? response.data : response;
          const items = envelope?.data ?? [];
          const currentPage = envelope?.current_page ?? page;
          const hasMore = currentPage < (envelope?.last_page ?? currentPage);
          return { items, currentPage, hasMore };
        }));
    }

    const endpoint = type === this.TYPE_CUSTOMERS ? 'customers/search' : 'subcontractors/search';
    return this.backendApiService.postApi(`${endpoint}?search=${encodeURIComponent(search)}`, '')
      .pipe(map((response: any) => ({ items: response?.data ?? [], currentPage: 1, hasMore: false })));
  }

  /** First open of a tab fetches its list; later visits reuse what is loaded. */
  private loadTab(type: string): void {
    if (!this.listFor(type).length) {
      this.fetchPage(type, '', 1);
    }
  }

  onSearchInput(type: string, event: any): void {
    this.fetchPage(type, event?.target?.value ?? '', 1);
  }

  onListScroll(type: string, event: any): void {
    if (!this.PAGINATED_TYPES.includes(type)) {
      return;
    }
    if (this.isLoading[type] || this.isLoadingMore[type] || !this.hasMore[type]) {
      return;
    }
    const el = event.target;
    const threshold = 40;
    if (el.scrollTop + el.offsetHeight >= el.scrollHeight - threshold) {
      this.fetchPage(type, this.searchTerm[type], this.currentPage[type] + 1);
    }
  }

  close(data: any = '') {
    this.dialogRef.close(data);
  }

  getActiveTab(event: any){
    this.selectedIndex = event;
    this.loadTab(this.visibleTabs[event]);
  }

  onItemClick(type: string, user: any): void {
    if (!this.multiple) {
      this.close({type: this.type, user: user, index: this.index, selectedTab: this.getTabNameUsingTabIndex(this.selectedIndex)});
      return;
    }
    const entity = this.toEntity(type, user);
    const key = ticketPeopleKey(entity);
    if (this.existingKeys.has(key)) {
      return;
    }
    if (this.selected.has(key)) {
      this.selected.delete(key);
    } else {
      this.selected.set(key, entity);
    }
  }

  isSelected(type: string, user: any): boolean {
    const key = ticketPeopleKey({ type, id: user?.id });
    return this.selected.has(key) || this.existingKeys.has(key);
  }

  isExisting(type: string, user: any): boolean {
    return this.existingKeys.has(ticketPeopleKey({ type, id: user?.id }));
  }

  /** Tab label with the number of new picks on it, so picks on other tabs stay visible. */
  tabLabel(type: string): string {
    if (!this.multiple) {
      return type;
    }
    let count = 0;
    this.selected.forEach((entity: any) => { if (entity.type === type) count++; });
    return count ? `${type} (${count})` : type;
  }

  confirmSelection(): void {
    if (!this.selected.size) {
      return;
    }
    this.close({ type: this.type, index: this.index, items: Array.from(this.selected.values()) });
  }

  /** The slim {id, type, full_name} shape checklists store and post back. */
  private toEntity(type: string, user: any): any {
    return { id: user?.id, type, full_name: this.getDisplayName(type, user), email: user?.email || '' };
  }

  getTabNameUsingTabIndex(index: any) {
    let value = '';
    switch (index) {
      case 0:
        value = this.TYPE_USER;
        break;
      case 1:
        value = this.TYPE_VENDORS;
        break;
      case 2:
        value = this.TYPE_CUSTOMERS
        break;
      case 3:
        value = this.TYPE_SUBCONTRACTORS;
        break;
      default:
    }
    return value;
  }

  checkAttachAbleUser(type: string){
    return this.attachAbleUser.includes(type);
  }

  getDisplayName(type: string, user: any): string {
    if (type === this.TYPE_CUSTOMERS) return user?.company_name || '';
    if (type === this.TYPE_USER) return user?.full_name || '';
    return user?.name || '';
  }

  /** Normalizes whichever name field the entity uses onto full_name, the field app-user-hover-card reads. */
  getHoverCardUser(type: string, user: any): any {
    return { ...user, full_name: this.getDisplayName(type, user) };
  }

}
